import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Phone, Mail, MapPin, Clock, MessageCircle, Heart, Shield, 
  Users, Brain, CheckCircle, ArrowRight, Menu, X, Calendar,
  ChevronDown, Star, Sparkles, HandHeart, HeartHandshake, ArrowLeft
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation Variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: "#servicios", label: "Servicios" },
    { href: "#metodo", label: "Método" },
    { href: "#testimonios", label: "Testimonios" },
    { href: "#faq", label: "FAQ" },
    { href: "#contacto", label: "Contacto" }
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/90 backdrop-blur-md shadow-lg' 
          : 'bg-transparent'
      }`}
      data-testid="navigation-header"
    >
      <nav className="max-w-7xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
        <a href="#hero" className="flex items-center gap-3" data-testid="nav-logo">
          <div className="w-10 h-10 rounded-full bg-[#4A6741] flex items-center justify-center">
            <HeartHandshake className="w-5 h-5 text-white" />
          </div>
          <div>
            <span className="font-playfair font-bold text-lg text-[#2D3748]">Terapia de Pareja Málaga</span>
            <span className="block text-xs text-[#718096]">Casaleiz Psicología</span>
          </div>
        </a>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-[#4A5568] hover:text-[#4A6741] transition-colors font-medium"
              data-testid={`nav-link-${link.label.toLowerCase()}`}
            >
              {link.label}
            </a>
          ))}
          <a
            href="tel:644299079"
            className="btn-primary text-sm py-2.5 px-5"
            data-testid="nav-cta-button"
          >
            <Phone className="w-4 h-4 mr-2" />
            Llamar
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden p-2"
          data-testid="mobile-menu-button"
        >
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t"
            data-testid="mobile-menu"
          >
            <div className="px-5 py-4 space-y-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="block py-2 text-[#4A5568] hover:text-[#4A6741]"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
              <a href="tel:644299079" className="btn-primary w-full text-center block">
                <Phone className="w-4 h-4 mr-2 inline" /> Llamar ahora
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

// Hero Section
const HeroSection = () => (
  <section id="hero" className="min-h-screen bg-gradient-hero relative overflow-hidden pt-20" data-testid="hero-section">
    {/* Background decorative elements */}
    <div className="absolute top-20 right-0 w-96 h-96 bg-[#4A6741]/5 rounded-full blur-3xl" />
    <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#C67D63]/5 rounded-full blur-3xl" />
    
    <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 py-16 md:py-24 lg:py-32">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Text Content */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="order-2 lg:order-1"
        >
          <motion.span
            variants={fadeInUp}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#4A6741]/10 text-[#4A6741] text-sm font-medium mb-6"
          >
            <MapPin className="w-4 h-4" />
            Centro de Terapia en Málaga
          </motion.span>
          
          <motion.h1
            variants={fadeInUp}
            className="font-playfair text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-tight mb-6 text-[#2D3748]"
          >
            Terapia de Pareja<br />
            <span className="text-gradient">en Málaga</span>
          </motion.h1>
          
          <motion.p
            variants={fadeInUp}
            className="text-lg md:text-xl text-[#4A5568] leading-relaxed mb-8 max-w-xl"
          >
            Especialistas en ayudar a parejas a encontrar el equilibrio, superar crisis 
            y construir relaciones auténticas, alegres y duraderas basadas en el amor.
          </motion.p>
          
          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 mb-8">
            <a href="#reservar" className="btn-accent" data-testid="hero-cta-primary">
              <Calendar className="w-5 h-5 mr-2" />
              Reservar Cita
            </a>
            <a href="tel:644299079" className="btn-outline" data-testid="hero-cta-secondary">
              <Phone className="w-5 h-5 mr-2" />
              644 299 079
            </a>
          </motion.div>
          
          <motion.div variants={fadeInUp} className="flex items-center gap-6 text-sm text-[#718096]">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-[#4A6741]" />
              Sesiones de 60 min
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-[#4A6741]" />
              Terapia ACT
            </div>
          </motion.div>
        </motion.div>
        
        {/* Hero Image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="order-1 lg:order-2 relative"
        >
          <div className="relative rounded-3xl overflow-hidden shadow-2xl">
            <img
              src="https://images.unsplash.com/photo-1769230376197-a7bc284a9c3b?w=800&q=80"
              alt="Pareja abrazándose al atardecer representando conexión y amor en terapia de pareja"
              className="w-full h-auto object-cover aspect-[4/3]"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2D3748]/20 to-transparent" />
          </div>
          
          {/* Floating Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl hidden md:block"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#4A6741]/10 flex items-center justify-center">
                <Star className="w-6 h-6 text-[#4A6741]" />
              </div>
              <div>
                <p className="font-bold text-[#2D3748]">+500 parejas</p>
                <p className="text-sm text-[#718096]">han confiado en nosotros</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
    
    {/* Scroll Indicator */}
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1 }}
      className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center"
    >
      <span className="text-sm text-[#718096] mb-2">Descubre más</span>
      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 1.5 }}
      >
        <ChevronDown className="w-6 h-6 text-[#4A6741]" />
      </motion.div>
    </motion.div>
  </section>
);

// Why It Happens Section
const WhySection = () => (
  <section id="por-que" className="section-padding bg-white" data-testid="why-section">
    <div className="max-w-7xl mx-auto container-padding">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block px-4 py-2 rounded-full bg-[#C67D63]/10 text-[#C67D63] text-sm font-medium mb-4">
            Comprendemos tu situación
          </span>
          <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl text-[#2D3748] mb-6">
            ¿Por qué ocurren las crisis de pareja?
          </h2>
          <p className="text-lg text-[#4A5568] leading-relaxed mb-6">
            Las relaciones pueden hacernos increíblemente felices y profundamente infelices. 
            Todos evolucionamos a lo largo del tiempo, al ritmo de nuestros cambios personales 
            y profesionales, experiencias y acontecimientos.
          </p>
          <p className="text-lg text-[#4A5568] leading-relaxed mb-8">
            Situaciones como preocupaciones laborales, problemas de salud, dificultades económicas 
            o la llegada de los hijos pueden alterar el equilibrio de la pareja. Cuando la complicidad 
            y el diálogo se reducen, la <strong>terapia de pareja en Málaga</strong> puede ayudar.
          </p>
          <a href="#servicios" className="btn-primary inline-flex" data-testid="why-cta">
            Ver síntomas <ArrowRight className="w-4 h-4 ml-2" />
          </a>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          <img
            src="https://images.unsplash.com/photo-1766735329061-f4f21e7b50b4?w=700&q=80"
            alt="Pareja caminando juntos en la naturaleza representando el camino hacia la reconciliación"
            className="rounded-3xl shadow-xl w-full object-cover aspect-[4/3]"
            loading="lazy"
          />
          <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-[#4A6741]/10 rounded-full blur-2xl" />
        </motion.div>
      </div>
    </div>
  </section>
);

// Symptoms Section (Bento Grid)
const SymptomsSection = () => {
  const symptoms = [
    { icon: MessageCircle, title: "Discusiones continuas", desc: "Conflictos frecuentes sin resolución" },
    { icon: Heart, title: "Problemas de intimidad", desc: "Dificultades en la conexión física y emocional" },
    { icon: Shield, title: "Desconfianza", desc: "Falta de seguridad en la relación" },
    { icon: Brain, title: "Ansiedad y estrés", desc: "Tensión constante en la relación" },
    { icon: Users, title: "Infidelidad", desc: "Ruptura de la confianza por terceros" },
    { icon: Sparkles, title: "Pérdida de ilusión", desc: "Falta de conexión y complicidad" }
  ];

  return (
    <section id="servicios" className="section-padding bg-[#F5F2EA]" data-testid="symptoms-section">
      <div className="max-w-7xl mx-auto container-padding">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span
            variants={fadeInUp}
            className="inline-block px-4 py-2 rounded-full bg-[#4A6741]/10 text-[#4A6741] text-sm font-medium mb-4"
          >
            Síntomas de crisis
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="font-playfair text-3xl md:text-4xl lg:text-5xl text-[#2D3748] mb-4"
          >
            ¿Tu pareja está en crisis?
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-lg text-[#4A5568] max-w-2xl mx-auto"
          >
            Identificar los síntomas es el primer paso hacia la recuperación de tu relación
          </motion.p>
        </motion.div>

        {/* Bento Grid */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {symptoms.map((symptom, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="bg-white rounded-2xl p-6 card-hover border border-[#F5F2EA]"
              data-testid={`symptom-card-${index}`}
            >
              <div className="w-14 h-14 rounded-2xl bg-[#4A6741]/10 flex items-center justify-center mb-4">
                <symptom.icon className="w-7 h-7 text-[#4A6741]" />
              </div>
              <h3 className="font-playfair text-xl text-[#2D3748] mb-2">{symptom.title}</h3>
              <p className="text-[#718096]">{symptom.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <a href="#reservar" className="btn-accent" data-testid="symptoms-cta">
            Solicitar ayuda profesional
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// Method Section (ACT Therapy)
const MethodSection = () => (
  <section id="metodo" className="section-padding bg-white" data-testid="method-section">
    <div className="max-w-7xl mx-auto container-padding">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative order-2 lg:order-1"
        >
          <img
            src="https://images.unsplash.com/photo-1749741993874-bc375dbcaa17?w=700&q=80"
            alt="Pareja caminando de la mano en la playa simbolizando el camino juntos en terapia"
            className="rounded-3xl shadow-xl w-full object-cover aspect-[4/3]"
            loading="lazy"
          />
          <div className="absolute -top-4 -left-4 w-24 h-24 bg-[#C67D63]/20 rounded-full blur-xl" />
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="order-1 lg:order-2"
        >
          <motion.span
            variants={fadeInUp}
            className="inline-block px-4 py-2 rounded-full bg-[#4A6741]/10 text-[#4A6741] text-sm font-medium mb-4"
          >
            Nuestro método
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="font-playfair text-3xl md:text-4xl lg:text-5xl text-[#2D3748] mb-6"
          >
            Terapia ACT: Aceptación y Compromiso
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-[#4A5568] leading-relaxed mb-6">
            Utilizamos la <strong>Terapia de Aceptación y Compromiso (ACT)</strong>, una psicoterapia 
            con sólidos fundamentos científicos y de gran eficacia, especialmente diseñada para 
            ayudar a parejas en Málaga.
          </motion.p>

          <motion.div variants={fadeInUp} className="space-y-4 mb-8">
            {[
              "Dejar a un lado pensamientos innecesarios y actitudes negativas",
              "Manejar las emociones de manera más efectiva",
              "Superar el miedo, la ira, la frustración y el resentimiento",
              "Perdonarse mutuamente y reconstruir la confianza",
              "Actuar de acuerdo con sus valores para reconstruir la relación"
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#4A6741] mt-1 flex-shrink-0" />
                <span className="text-[#4A5568]">{item}</span>
              </div>
            ))}
          </motion.div>

          <motion.a
            variants={fadeInUp}
            href="tel:644299079"
            className="btn-primary inline-flex"
            data-testid="method-cta"
          >
            <Phone className="w-5 h-5 mr-2" />
            Consultar ahora
          </motion.a>
        </motion.div>
      </div>
    </div>
  </section>
);

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      text: "Es sobre todo un gran profesional y te ayuda mucho… una atención personalizada para cada situación… MUCHAS GRACIAS.",
      author: "Francis G.",
      role: "Paciente"
    },
    {
      text: "Ha sido una primera cita muy buena. Hemos podido hablar de todo y nos ha ayudado a dar los primeros pasos. Muy contentos.",
      author: "J. Ortigosa",
      role: "Paciente"
    },
    {
      text: "Llevo varias sesiones y me gusta mucho cómo aplica la Terapia de Aceptación y Compromiso. Es una persona cercana y positiva. Muy recomendable.",
      author: "Carolina C.",
      role: "Paciente"
    }
  ];

  return (
    <section id="testimonios" className="section-padding bg-[#F5F2EA]" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto container-padding">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span
            variants={fadeInUp}
            className="inline-block px-4 py-2 rounded-full bg-[#C67D63]/10 text-[#C67D63] text-sm font-medium mb-4"
          >
            Testimonios
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="font-playfair text-3xl md:text-4xl lg:text-5xl text-[#2D3748] mb-4"
          >
            Parejas felices en Málaga
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-[#4A5568] max-w-2xl mx-auto">
            Lo que dicen nuestros pacientes sobre la terapia de pareja
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid md:grid-cols-3 gap-8"
        >
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="bg-white rounded-2xl p-8 shadow-lg relative"
              data-testid={`testimonial-card-${index}`}
            >
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-[#C67D63] fill-[#C67D63]" />
                ))}
              </div>
              <p className="text-[#4A5568] mb-6 italic">"{testimonial.text}"</p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#4A6741]/10 flex items-center justify-center">
                  <span className="font-bold text-[#4A6741]">{testimonial.author[0]}</span>
                </div>
                <div>
                  <p className="font-semibold text-[#2D3748]">{testimonial.author}</p>
                  <p className="text-sm text-[#718096]">{testimonial.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  
  const faqs = [
    {
      question: "¿Cuál es la duración de la Terapia de pareja?",
      answer: "La Terapia de pareja tiene una duración de 60 minutos por sesión."
    },
    {
      question: "¿Funciona la terapia si uno de los miembros no está de acuerdo?",
      answer: "No, ambos deben estar motivados. Es como ir al médico y luego no tomar los medicamentos que prescribe. Los comportamientos que se deben mantener en pareja requieren la participación de ambos miembros."
    },
    {
      question: "¿Mejorará la situación de la pareja con la terapia?",
      answer: "No necesariamente. Si la relación no se basa en nada sólido, con dos personas totalmente opuestas, la mejor solución puede ser terminar la relación de forma sana. La terapia se basa en la historia de la pareja y en los puntos en común."
    },
    {
      question: "¿La terapia de pareja involucra a los niños?",
      answer: "Absolutamente no. La terapia de pareja involucra solo a la pareja y se basa en la relación entre los dos miembros, incluso si son padres."
    },
    {
      question: "¿Cuánto tiempo dura una terapia de pareja completa?",
      answer: "Si la terapia funciona, una vez establecido el obstáculo a la relación, los resultados comienzan a verse incluso después de algunas semanas. En general, la terapia nunca es muy larga."
    }
  ];

  return (
    <section id="faq" className="section-padding bg-white" data-testid="faq-section">
      <div className="max-w-4xl mx-auto container-padding">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span
            variants={fadeInUp}
            className="inline-block px-4 py-2 rounded-full bg-[#4A6741]/10 text-[#4A6741] text-sm font-medium mb-4"
          >
            Preguntas frecuentes
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="font-playfair text-3xl md:text-4xl lg:text-5xl text-[#2D3748] mb-4"
          >
            Resolvemos tus dudas
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-[#4A5568]">
            Encuentra respuestas a las preguntas más comunes sobre la terapia de pareja en Málaga
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="space-y-4"
        >
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="bg-[#F5F2EA] rounded-2xl overflow-hidden"
              data-testid={`faq-item-${index}`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left"
                data-testid={`faq-button-${index}`}
              >
                <span className="font-semibold text-[#2D3748] pr-4">{faq.question}</span>
                <ChevronDown
                  className={`w-5 h-5 text-[#4A6741] transition-transform ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <p className="px-6 pb-5 text-[#4A5568]">{faq.answer}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Blog Section
const BlogSection = ({ posts }) => (
  <section id="blog" className="section-padding bg-[#F5F2EA]" data-testid="blog-section">
    <div className="max-w-7xl mx-auto container-padding">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
        className="text-center mb-16"
      >
        <motion.span
          variants={fadeInUp}
          className="inline-block px-4 py-2 rounded-full bg-[#C67D63]/10 text-[#C67D63] text-sm font-medium mb-4"
        >
          Blog
        </motion.span>
        <motion.h2
          variants={fadeInUp}
          className="font-playfair text-3xl md:text-4xl lg:text-5xl text-[#2D3748] mb-4"
        >
          Últimas noticias y recursos
        </motion.h2>
        <motion.p variants={fadeInUp} className="text-lg text-[#4A5568] max-w-2xl mx-auto">
          Artículos y consejos sobre relaciones de pareja y bienestar emocional
        </motion.p>
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
        className="grid md:grid-cols-3 gap-8"
      >
        {posts.map((post, index) => (
          <motion.article
            key={post.id}
            variants={fadeInUp}
            className="bg-white rounded-2xl overflow-hidden shadow-lg card-hover"
            data-testid={`blog-card-${index}`}
          >
            <Link to={`/blog/${post.slug}`} className="block">
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={post.imagen_url}
                  alt={post.titulo}
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <span className="text-xs font-medium text-[#C67D63] uppercase tracking-wider">
                  {post.categoria}
                </span>
                <h3 className="font-playfair text-xl text-[#2D3748] mt-2 mb-3 line-clamp-2 hover:text-[#4A6741] transition-colors">
                  {post.titulo}
                </h3>
                <p className="text-[#718096] text-sm line-clamp-3">{post.extracto}</p>
                <span className="inline-flex items-center mt-4 text-[#4A6741] font-medium text-sm">
                  Leer más <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </div>
            </Link>
          </motion.article>
        ))}
      </motion.div>
    </div>
  </section>
);

// Blog Post Page Component
const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/${slug}`);
        setPost(response.data);
      } catch (err) {
        setError('Artículo no encontrado');
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#4A6741] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex flex-col items-center justify-center">
        <h1 className="font-playfair text-3xl text-[#2D3748] mb-4">Artículo no encontrado</h1>
        <Link to="/" className="btn-primary">
          <ArrowLeft className="w-4 h-4 mr-2" /> Volver al inicio
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Navigation */}
      <header className="bg-white/90 backdrop-blur-md shadow-sm sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <div className="w-10 h-10 rounded-full bg-[#4A6741] flex items-center justify-center">
              <HeartHandshake className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-playfair font-bold text-lg text-[#2D3748]">Terapia de Pareja Málaga</span>
              <span className="block text-xs text-[#718096]">Casaleiz Psicología</span>
            </div>
          </Link>
          <a href="tel:644299079" className="btn-primary text-sm py-2.5 px-5">
            <Phone className="w-4 h-4 mr-2" />
            Llamar
          </a>
        </nav>
      </header>

      {/* Article */}
      <article className="max-w-4xl mx-auto px-5 md:px-8 py-12">
        {/* Back button */}
        <button 
          onClick={() => navigate(-1)}
          className="inline-flex items-center text-[#4A6741] hover:text-[#3a5731] mb-8 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Volver
        </button>

        {/* Category */}
        <span className="inline-block px-4 py-2 rounded-full bg-[#C67D63]/10 text-[#C67D63] text-sm font-medium mb-4">
          {post.categoria}
        </span>

        {/* Title */}
        <h1 className="font-playfair text-3xl md:text-4xl lg:text-5xl text-[#2D3748] mb-6 leading-tight">
          {post.titulo}
        </h1>

        {/* Meta */}
        <div className="flex items-center gap-4 text-[#718096] text-sm mb-8">
          <span>Por {post.autor}</span>
          <span>•</span>
          <span>{new Date(post.created_at).toLocaleDateString('es-ES', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}</span>
        </div>

        {/* Featured Image */}
        <div className="rounded-2xl overflow-hidden mb-10 shadow-lg">
          <img 
            src={post.imagen_url} 
            alt={post.titulo}
            className="w-full h-auto object-cover aspect-[16/9]"
          />
        </div>

        {/* Content */}
        <div className="prose prose-lg max-w-none">
          {post.contenido.split('\n\n').map((paragraph, index) => (
            <p key={index} className="text-[#4A5568] leading-relaxed mb-6 text-lg">
              {paragraph}
            </p>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 p-8 bg-[#F5F2EA] rounded-2xl text-center">
          <h3 className="font-playfair text-2xl text-[#2D3748] mb-4">
            ¿Necesitas ayuda profesional?
          </h3>
          <p className="text-[#4A5568] mb-6">
            Nuestro equipo de psicólogos especializados en terapia de pareja en Málaga está aquí para ayudarte.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/#reservar" className="btn-accent">
              <Calendar className="w-5 h-5 mr-2" /> Reservar cita
            </Link>
            <a href="tel:644299079" className="btn-outline">
              <Phone className="w-5 h-5 mr-2" /> 644 299 079
            </a>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-[#1a202c] text-white py-8 mt-12">
        <div className="max-w-7xl mx-auto px-5 text-center">
          <p className="text-white/60 text-sm">
            © {new Date().getFullYear()} Terapia de Pareja Málaga - Casaleiz Psicología
          </p>
        </div>
      </footer>

      <WhatsAppButton />
    </div>
  );
};

// Booking Section
const BookingSection = () => {
  const [formData, setFormData] = useState({
    nombre: '', email: '', telefono: '', fecha_preferida: '', hora_preferida: '', motivo: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/appointments`, formData);
      setSubmitStatus('success');
      setFormData({ nombre: '', email: '', telefono: '', fecha_preferida: '', hora_preferida: '', motivo: '' });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="reservar" className="section-padding bg-white" data-testid="booking-section">
      <div className="max-w-4xl mx-auto container-padding">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-12"
        >
          <motion.span
            variants={fadeInUp}
            className="inline-block px-4 py-2 rounded-full bg-[#4A6741]/10 text-[#4A6741] text-sm font-medium mb-4"
          >
            Reserva tu cita
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="font-playfair text-3xl md:text-4xl lg:text-5xl text-[#2D3748] mb-4"
          >
            Comienza tu camino hacia una relación mejor
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-[#4A5568]">
            Rellena el formulario y te contactaremos para confirmar tu cita
          </motion.p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          onSubmit={handleSubmit}
          className="bg-[#F5F2EA] rounded-3xl p-8 md:p-10"
          data-testid="booking-form"
        >
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-[#2D3748] mb-2">Nombre completo *</label>
              <input
                type="text"
                required
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                className="input-elegant"
                placeholder="Tu nombre"
                data-testid="booking-input-nombre"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2D3748] mb-2">Email *</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input-elegant"
                placeholder="tu@email.com"
                data-testid="booking-input-email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2D3748] mb-2">Teléfono *</label>
              <input
                type="tel"
                required
                value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                className="input-elegant"
                placeholder="644 299 079"
                data-testid="booking-input-telefono"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2D3748] mb-2">Fecha preferida *</label>
              <input
                type="date"
                required
                value={formData.fecha_preferida}
                onChange={(e) => setFormData({ ...formData, fecha_preferida: e.target.value })}
                className="input-elegant"
                data-testid="booking-input-fecha"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2D3748] mb-2">Hora preferida *</label>
              <select
                required
                value={formData.hora_preferida}
                onChange={(e) => setFormData({ ...formData, hora_preferida: e.target.value })}
                className="input-elegant"
                data-testid="booking-input-hora"
              >
                <option value="">Selecciona una hora</option>
                <option value="09:00">09:00</option>
                <option value="10:00">10:00</option>
                <option value="11:00">11:00</option>
                <option value="12:00">12:00</option>
                <option value="16:00">16:00</option>
                <option value="17:00">17:00</option>
                <option value="18:00">18:00</option>
                <option value="19:00">19:00</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[#2D3748] mb-2">Motivo de la consulta</label>
              <textarea
                value={formData.motivo}
                onChange={(e) => setFormData({ ...formData, motivo: e.target.value })}
                className="input-elegant min-h-[100px] resize-none"
                placeholder="Cuéntanos brevemente el motivo de tu consulta..."
                data-testid="booking-input-motivo"
              />
            </div>
          </div>

          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700" data-testid="booking-success">
              ¡Gracias! Hemos recibido tu solicitud. Te contactaremos pronto para confirmar tu cita.
            </div>
          )}
          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700" data-testid="booking-error">
              Ha ocurrido un error. Por favor, inténtalo de nuevo o llámanos directamente.
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-accent w-full md:w-auto"
            data-testid="booking-submit-button"
          >
            {isSubmitting ? 'Enviando...' : 'Solicitar cita'}
            <Calendar className="w-5 h-5 ml-2" />
          </button>
        </motion.form>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({ nombre: '', email: '', telefono: '', mensaje: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus('success');
      setFormData({ nombre: '', email: '', telefono: '', mensaje: '' });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contacto" className="section-padding bg-[#2D3748] text-white relative overflow-hidden" data-testid="contact-section">
      <div className="absolute inset-0 opacity-10">
        <img
          src="https://images.unsplash.com/photo-1633877759460-6adc66410207?w=1200&q=60"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      
      <div className="max-w-7xl mx-auto container-padding relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Info */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.span
              variants={fadeInUp}
              className="inline-block px-4 py-2 rounded-full bg-white/10 text-white text-sm font-medium mb-4"
            >
              Contacto
            </motion.span>
            <motion.h2
              variants={fadeInUp}
              className="font-playfair text-3xl md:text-4xl lg:text-5xl mb-6"
            >
              Estamos en Málaga para ayudarte
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-white/80 mb-10">
              No dudes en contactarnos. Estaremos encantados de atenderte y resolver todas tus dudas 
              sobre la terapia de pareja.
            </motion.p>

            <motion.div variants={staggerContainer} className="space-y-6">
              <motion.div variants={fadeInUp} className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#4A6741] flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-semibold mb-1">Dirección</p>
                  <p className="text-white/70">Don Cristian 19, 4ºB<br />29007 Málaga, España</p>
                </div>
              </motion.div>

              <motion.div variants={fadeInUp} className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#4A6741] flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-semibold mb-1">Teléfono</p>
                  <a href="tel:644299079" className="text-white/70 hover:text-white transition-colors">
                    644 299 079
                  </a>
                </div>
              </motion.div>

              <motion.div variants={fadeInUp} className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#4A6741] flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-semibold mb-1">Email</p>
                  <a href="mailto:carlos.casaleiz@gmail.com" className="text-white/70 hover:text-white transition-colors">
                    carlos.casaleiz@gmail.com
                  </a>
                </div>
              </motion.div>

              <motion.div variants={fadeInUp} className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#4A6741] flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-semibold mb-1">Horario</p>
                  <p className="text-white/70">Lunes a Viernes: 9:00 – 20:00<br />Sábados y Domingos: Cerrado</p>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Contact Form */}
          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onSubmit={handleSubmit}
            className="bg-white rounded-3xl p-8 text-[#2D3748]"
            data-testid="contact-form"
          >
            <h3 className="font-playfair text-2xl mb-6">Envíanos un mensaje</h3>
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium mb-2">Nombre *</label>
                <input
                  type="text"
                  required
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  className="input-elegant"
                  placeholder="Tu nombre"
                  data-testid="contact-input-nombre"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-elegant"
                  placeholder="tu@email.com"
                  data-testid="contact-input-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Teléfono</label>
                <input
                  type="tel"
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  className="input-elegant"
                  placeholder="644 299 079"
                  data-testid="contact-input-telefono"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Mensaje *</label>
                <textarea
                  required
                  value={formData.mensaje}
                  onChange={(e) => setFormData({ ...formData, mensaje: e.target.value })}
                  className="input-elegant min-h-[120px] resize-none"
                  placeholder="¿En qué podemos ayudarte?"
                  data-testid="contact-input-mensaje"
                />
              </div>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-700" data-testid="contact-success">
                  ¡Mensaje enviado! Te responderemos lo antes posible.
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700" data-testid="contact-error">
                  Error al enviar. Inténtalo de nuevo o llámanos.
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full"
                data-testid="contact-submit-button"
              >
                {isSubmitting ? 'Enviando...' : 'Enviar mensaje'}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => (
  <footer className="bg-[#1a202c] text-white py-12" data-testid="footer">
    <div className="max-w-7xl mx-auto container-padding">
      <div className="grid md:grid-cols-4 gap-8 mb-8">
        {/* Brand */}
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[#4A6741] flex items-center justify-center">
              <HeartHandshake className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-playfair font-bold text-lg">Terapia de Pareja Málaga</span>
              <span className="block text-xs text-white/60">Casaleiz Psicología</span>
            </div>
          </div>
          <p className="text-white/70 text-sm max-w-md">
            Centro especializado en Terapia de Pareja en Málaga. Ayudamos a parejas a encontrar 
            el equilibrio y construir relaciones auténticas y duraderas.
          </p>
        </div>

        {/* Quick Links */}
        <div>
          <h4 className="font-semibold mb-4">Enlaces</h4>
          <ul className="space-y-2 text-sm text-white/70">
            <li><a href="#servicios" className="hover:text-white transition-colors">Servicios</a></li>
            <li><a href="#metodo" className="hover:text-white transition-colors">Método ACT</a></li>
            <li><a href="#testimonios" className="hover:text-white transition-colors">Testimonios</a></li>
            <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
            <li><a href="#contacto" className="hover:text-white transition-colors">Contacto</a></li>
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h4 className="font-semibold mb-4">Contacto</h4>
          <ul className="space-y-2 text-sm text-white/70">
            <li>Don Cristian 19, 4ºB</li>
            <li>29007 Málaga, España</li>
            <li><a href="tel:644299079" className="hover:text-white transition-colors">644 299 079</a></li>
            <li><a href="mailto:carlos.casaleiz@gmail.com" className="hover:text-white transition-colors">carlos.casaleiz@gmail.com</a></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-white/50">
        <p>© {new Date().getFullYear()} Terapia de Pareja Málaga - Casaleiz Psicología. Todos los derechos reservados.</p>
        <p className="mt-2 md:mt-0">Psicólogos especialistas en Málaga</p>
      </div>
    </div>
  </footer>
);

// WhatsApp Float Button
const WhatsAppButton = () => (
  <a
    href="https://wa.me/34644299079?text=Hola,%20me%20gustaría%20información%20sobre%20la%20terapia%20de%20pareja"
    target="_blank"
    rel="noopener noreferrer"
    className="whatsapp-float animate-pulse-gentle"
    aria-label="Contactar por WhatsApp"
    data-testid="whatsapp-button"
  >
    <svg viewBox="0 0 24 24" className="w-8 h-8 text-white fill-current">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
    </svg>
  </a>
);

// Main Home Component
const Home = () => {
  const [blogPosts, setBlogPosts] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed blog posts if needed
        await axios.post(`${API}/seed-blog`);
        // Fetch blog posts
        const response = await axios.get(`${API}/blog`);
        setBlogPosts(response.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="grain-overlay">
      <Navigation />
      <main>
        <HeroSection />
        <WhySection />
        <SymptomsSection />
        <MethodSection />
        <TestimonialsSection />
        <FAQSection />
        {blogPosts.length > 0 && <BlogSection posts={blogPosts} />}
        <BookingSection />
        <ContactSection />
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="*" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
